import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { CLINIC, WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

const HERO_IMAGE =
  "https://customer-assets-0z36b82j.emergentagent.net/job_movimento-saude-1/artifacts/4perqopx_fachadaaa.jpeg";

export const Hero = () => {
  const { t } = useT();
  const scrollToContact = () => {
    const el = document.getElementById("contacto");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100svh] lg:min-h-[92vh] w-full overflow-hidden bg-[#1a1f1c]"
    >
      {/* Background image — full bleed */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Fachada da clínica Yin Motion em Tavira"
          className="w-full h-full object-cover object-[60%_62%] md:object-[center_62%]"
        />
        {/* Readability overlays — 20% lighter */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1f1c]/70 via-[#1a1f1c]/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1f1c]/35 via-transparent to-[#1a1f1c]/15" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-[100svh] lg:min-h-[92vh] flex flex-col">
        <div className="flex-1 flex items-center pt-36 md:pt-44 pb-20 md:pb-32">
          <div className="w-full max-w-[1200px] mx-auto px-5 md:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="max-w-[760px]"
            >
              <div className="flex items-center gap-3 mb-10">
                <span className="h-px w-10 bg-[#EE8984]" />
                <span className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#F7DEDA]">
                  {t("hero.eyebrow")}
                </span>
              </div>

              <h1 className="font-serif text-[40px] sm:text-5xl lg:text-[60px] xl:text-[72px] 2xl:text-[80px] leading-[1.05] tracking-tight text-white">
                <span className="block font-normal">
                  {t("hero.titleLine1")}
                </span>
                <span className="block font-bold text-white mt-2">
                  {t("hero.titleLine2")}
                </span>
              </h1>

              <p className="mt-10 md:mt-12 text-base md:text-lg text-white/80 leading-relaxed max-w-xl">
                {t("hero.subtitle")}
              </p>

              <div className="mt-12 flex flex-col sm:flex-row gap-5 sm:gap-7">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-btn"
                  className="group inline-flex items-center justify-center gap-2 bg-[#EE8984] text-[#2A322C] px-9 py-4 text-sm font-bold hover:bg-white transition-colors"
                >
                  {t("hero.ctaWhatsapp")}
                  <ArrowUpRight
                    size={18}
                    className="group-hover:rotate-45 transition-transform duration-300"
                  />
                </a>
                <button
                  onClick={scrollToContact}
                  data-testid="hero-booking-btn"
                  className="group inline-flex items-center justify-center gap-2 bg-white/15 backdrop-blur-md border border-white/70 text-white px-9 py-4 text-sm font-bold hover:bg-white hover:text-[#2A322C] transition-all"
                >
                  {t("hero.ctaContact")}
                  <ArrowUpRight
                    size={18}
                    className="group-hover:rotate-45 transition-transform duration-300"
                  />
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom bar — lighter, cleaner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="relative border-t border-white/10"
        >
          <div className="max-w-[1200px] mx-auto px-5 md:px-6 py-7 md:py-8 flex flex-col md:flex-row md:items-center justify-between gap-6 items-center md:items-stretch">
            {/* Social proof */}
            <div
              className="flex flex-col sm:flex-row sm:items-center items-center gap-3 sm:gap-6 justify-center md:justify-start text-center md:text-left"
              data-testid="hero-social-proof"
            >
              <div className="flex items-center justify-center md:justify-start gap-2">
                <div className="flex items-center gap-1 text-[#EE8984]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-current" />
                  ))}
                </div>
                <div className="text-sm text-white">
                  <span className="font-semibold">{CLINIC.googleRating}</span>
                  <span className="text-white/70"> {t("hero.ratingGoogle")}</span>
                </div>
              </div>
              <span className="hidden sm:inline h-3 w-px bg-white/20" />
              <div className="text-sm text-white font-semibold">
                {t("hero.ratingReviews")}
              </div>
            </div>

            {/* +15 years stat */}
            <div
              data-testid="hero-floating-stat"
              className="flex items-center justify-center md:justify-start gap-4"
            >
              <div className="font-serif text-3xl md:text-4xl text-white leading-none font-semibold">
                +{CLINIC.yearsOfExperience}
              </div>
              <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] font-medium text-white/60 max-w-[180px] leading-relaxed text-left whitespace-pre-line">
                {t("hero.statSmall")}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
